import { useLocation } from "@tanstack/react-router";
import { useEffect } from "react";
import { create } from "zustand";

/**
 * Escape hatch for surfaces whose crumb label is not reachable from the URL or
 * from a synchronous store read.
 *
 * Most routes resolve inside `useGlobalBreadcrumbCrumbs` with no fetching. A few
 * only learn a name from a hook that owns network state, and that hook belongs
 * to the page — not to the global bar. Those pages publish the label here.
 *
 * Published against a crumb **key**, not "the leaf". `/automations/:id/edit`
 * needs its middle crumb named, and a leaf-only mechanism can never do that —
 * it would rewrite "Edit" instead. The key comes from `resolveStaticBreadcrumb`,
 * so a page names the crumb it actually owns.
 *
 * Also keyed by pathname, so a stale label from the previous route cannot paint
 * over the new one in the render between navigation and the next page's publish.
 */
interface GlobalBreadcrumbState {
  readonly pathname: string | null;
  readonly key: string | null;
  readonly label: string | null;
  readonly publish: (pathname: string, key: string, label: string | null) => void;
  readonly clear: (pathname: string) => void;
  /**
   * The trailing detail, tracked on its own pathname so publishing or clearing
   * a crumb label cannot wipe it. A page can own both — `/usage` names its leaf
   * *and* its window — and a shared `clear` would let whichever unmounted first
   * blank the other.
   */
  readonly detailPathname: string | null;
  readonly detail: string | null;
  readonly publishDetail: (pathname: string, detail: string | null) => void;
  readonly clearDetail: (pathname: string) => void;
}

export const useGlobalBreadcrumbStore = create<GlobalBreadcrumbState>((set) => ({
  pathname: null,
  key: null,
  label: null,
  publish: (pathname, key, label) => set({ pathname, key, label }),
  clear: (pathname) =>
    set((state) =>
      state.pathname === pathname ? { pathname: null, key: null, label: null } : state,
    ),
  detailPathname: null,
  detail: null,
  publishDetail: (detailPathname, detail) => set({ detailPathname, detail }),
  clearDetail: (pathname) =>
    set((state) =>
      state.detailPathname === pathname ? { detailPathname: null, detail: null } : state,
    ),
}));

/**
 * Publishes a label for one crumb of the current route, for as long as the
 * surface is mounted. Pass `null` while the name is still loading — the bar
 * keeps showing its static fallback rather than blanking.
 *
 * The pathname is read from the router rather than passed in: a caller that
 * rebuilds the path by hand can drift from the route it is actually on, and a
 * key mismatch silently drops the label instead of failing loudly.
 */
export function usePublishBreadcrumbLabel(crumbKey: string, label: string | null): void {
  const pathname = useLocation({ select: (location) => location.pathname });
  const publish = useGlobalBreadcrumbStore((store) => store.publish);
  const clear = useGlobalBreadcrumbStore((store) => store.clear);

  useEffect(() => {
    publish(pathname, crumbKey, label);
    return () => clear(pathname);
  }, [clear, crumbKey, label, pathname, publish]);
}

/**
 * Publishes the trailing detail for the current route — the qualifier that
 * follows the page name rather than renaming it.
 *
 * `/usage` is the case this exists for: upstream reads `Usage / Jul 28 to Aug
 * 26`, where the window is a detail *after* the current crumb, not the crumb
 * itself. Publishing it as a label would rewrite the page's name to a date.
 *
 * Pass `null` to show nothing; the bar simply omits the segment.
 */
export function usePublishBreadcrumbDetail(detail: string | null): void {
  const pathname = useLocation({ select: (location) => location.pathname });
  const publishDetail = useGlobalBreadcrumbStore((store) => store.publishDetail);
  const clearDetail = useGlobalBreadcrumbStore((store) => store.clearDetail);

  useEffect(() => {
    publishDetail(pathname, detail);
    return () => clearDetail(pathname);
  }, [clearDetail, detail, pathname, publishDetail]);
}

/**
 * Reads the published detail, ignoring anything left from another route.
 *
 * Returns the string itself rather than a wrapper: this is read through a
 * store selector on a component mounted over every route, and a selector that
 * builds a fresh object on each call re-renders forever and blanks the app.
 * That has happened here before — see the warning in `useGlobalBreadcrumbCrumbs`.
 */
export function selectPublishedDetailForPath(
  state: Pick<GlobalBreadcrumbState, "detailPathname" | "detail">,
  pathname: string,
): string | null {
  return state.detailPathname === pathname ? state.detail : null;
}

export interface PublishedBreadcrumbLabel {
  readonly key: string;
  readonly label: string;
}

/** Reads the published label, ignoring anything left over from another route. */
export function selectPublishedLabelForPath(
  state: Pick<GlobalBreadcrumbState, "pathname" | "key" | "label">,
  pathname: string,
): PublishedBreadcrumbLabel | null {
  if (state.pathname !== pathname || state.key === null || state.label === null) {
    return null;
  }
  return { key: state.key, label: state.label };
}
